// Global-filter helpers shared by the two boards that consume the filter bar
// (JobKanbanView on the shop floor, CSMWriteBack in manager mode).
//
// The filter bar is MULTI-SELECT: warehouses, resource filters and BP names are
// each a list, built one pick at a time, and every pick shows as its own
// removable chip. An EMPTY list always means "no filter" (show everything) —
// never "match nothing" — so the boards behave exactly as they did before the
// first chip is added.
//
// Multiple values within one filter are OR'd (a job in warehouse 01 OR 02);
// different filters are AND'd (warehouse 01/02 AND BP "Acme").
//
// Extracted here rather than inlined in the boards because both are
// Syncfusion-heavy and awkward to exercise directly — this is the part with the
// actual logic in it, and it is unit-tested (`jobFilters.test.ts`).

import { IJob } from "../types/IJob";
import { IOperation } from "../types/IOperation";

/** Case/whitespace-insensitive comparison key. */
const key = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase();

/**
 * Append `value` to `list` unless it is already there (or empty). Returns the
 * same array reference when nothing changes, so React/easy-peasy consumers
 * don't re-render on a duplicate pick.
 */
export const addUnique = (
  list: string[],
  value: string | null | undefined
): string[] => {
  const v = (value ?? "").trim();
  if (!v) return list;
  return list.some((existing) => key(existing) === key(v)) ? list : [...list, v];
};

/** Remove `value` from `list` (case-insensitively). */
export const removeValue = (list: string[], value: string): string[] =>
  list.filter((existing) => key(existing) !== key(value));

/** Selections made in the global filter bar. Empty/omitted list = no filter. */
export interface JobFilterSelection {
  warehouses?: string[];
  bpNames?: string[];
}

/**
 * Does the job sit in one of the selected warehouses? Mirrors the server-side
 * predicate (`JL.PoWarehouse = @PoWarehouse`), so filtering N warehouses on the
 * client returns exactly what N single-warehouse round trips would.
 */
export const matchesWarehouses = (
  job: Pick<IJob, "poWarehouse">,
  warehouses?: string[]
): boolean => {
  if (!warehouses?.length) return true;
  return warehouses.some((w) => key(w) === key(job.poWarehouse));
};

/**
 * Does the job belong to one of the selected BP names? Exact (case-insensitive)
 * match, not `contains`: the values are picked from a list built out of the
 * jobs themselves, so a substring match would silently pull in neighbouring
 * customers. The free-text search box is still a `contains` search.
 */
export const matchesBpNames = (
  job: Pick<IJob, "customerName">,
  bpNames?: string[]
): boolean => {
  if (!bpNames?.length) return true;
  return bpNames.some((n) => key(n) === key(job.customerName));
};

/**
 * The basic search box's rule: a case-insensitive `contains` on job number, stock code
 * and customer name. Both boards apply exactly these three fields (JobKanbanView in
 * memory, CSMWriteBack as an EJ2 Query), and smart search asks the same question
 * first — so "would basic search find this?" has one answer everywhere.
 */
export const BASIC_SEARCH_FIELDS = ["jobNumber", "stockCode", "customerName"] as const;

export const jobMatchesText = (
  job: Pick<IJob, "jobNumber" | "stockCode" | "customerName">,
  text: string
): boolean => {
  const needle = key(text);
  if (!needle) return false;
  return BASIC_SEARCH_FIELDS.some((f) => key(job[f]).includes(needle));
};

/** Apply every selection in the filter bar that the boards evaluate locally. */
export const filterJobs = <T extends Pick<IJob, "poWarehouse" | "customerName">>(
  jobs: T[],
  selection: JobFilterSelection
): T[] => {
  const { warehouses, bpNames } = selection;
  if (!warehouses?.length && !bpNames?.length) return jobs;

  return jobs.filter(
    (job) => matchesWarehouses(job, warehouses) && matchesBpNames(job, bpNames)
  );
};

/**
 * Union of the resource lists returned for each selected resource filter,
 * de-duplicated by `resourceNo` (the field the Kanban columns are keyed on) so
 * two filters that share a resource don't produce a duplicate column. Order of
 * first appearance is kept, which keeps the column order stable as chips are
 * added.
 */
export const mergeOperations = (lists: IOperation[][]): IOperation[] => {
  const seen = new Set<string>();
  const merged: IOperation[] = [];

  for (const list of lists) {
    for (const operation of list ?? []) {
      const id = key(operation?.resourceNo);
      if (!operation || seen.has(id)) continue;
      seen.add(id);
      merged.push(operation);
    }
  }

  return merged;
};

/**
 * The single warehouse to ask the server for. With exactly one chip we let the
 * database do the filtering (as before); with none or several we pull the full
 * board and narrow it locally — `filterJobs` then produces the same rows.
 */
export const serverWarehouse = (warehouses: string[]): string | null =>
  warehouses.length === 1 ? warehouses[0] : null;

/** Stable dependency key for a selection list (arrays change identity). */
export const selectionKey = (values: string[]): string => values.join("|");

/** The due-date presets the filter bar can stamp on a range. */
export type DuePreset = "TODAY" | "THIS_WEEK";

/**
 * The preset marker (`__preset__:THIS_WEEK`) carried alongside a date range, if
 * any. The filter bar's Today/This Week highlight is read back from here so it
 * survives a remount instead of being re-guessed from local state — an unknown
 * or missing marker means "custom range", not a preset.
 */
export const readDuePreset = (
  searchValue?: string | null
): DuePreset | null => {
  if (!searchValue) return null;

  for (const part of searchValue.split("|")) {
    const token = part.trim();
    if (!token.startsWith("__preset__")) continue;

    const value = token.split(":")[1];
    if (value === "TODAY" || value === "THIS_WEEK") return value;
  }

  return null;
};

export interface ParsedSearchValue {
  /** Inclusive `YYYY-MM-DD` start of the due-date range, if one is set. */
  from: string | null;
  /** Inclusive `YYYY-MM-DD` end of the due-date range, if one is set. */
  to: string | null;
  /** Free text typed into the search box, if any. */
  text: string | null;
}

/**
 * Parse the pipe-delimited `searchValue` the filter bar composes.
 *
 * Tokens wrapped in double underscores are structured (`__date__:from:to`,
 * `__preset__:THIS_WEEK`); anything else is the operator's free text. Every
 * `__token__` we don't understand is IGNORED rather than treated as text —
 * previously "the part that isn't `__date__`" was taken as the search text, so
 * the `__preset__` marker that the Today/This Week buttons prepend was matched
 * against job numbers and emptied the board.
 */
export const parseSearchValue = (
  searchValue?: string | null
): ParsedSearchValue => {
  const result: ParsedSearchValue = { from: null, to: null, text: null };
  if (!searchValue?.trim()) return result;

  for (const part of searchValue.split("|")) {
    const token = part.trim();
    if (!token) continue;

    if (token.startsWith("__date__")) {
      const [, from, to] = token.split(":");
      if (from && to) {
        result.from = from;
        result.to = to;
      }
      continue;
    }

    // Any other structured marker (e.g. __preset__, __week__) is not text.
    if (token.startsWith("__")) continue;

    if (result.text === null) result.text = token;
  }

  return result;
};
